import { movies, tvShows, varietyShows } from "@/mocks/catalog";
import type { CatalogItem, CatalogType, CategoryMeta } from "@/types/catalog";

export const allItems = [...movies, ...tvShows, ...varietyShows] as CatalogItem[];

export const categoryMetaMap: Record<CatalogType, CategoryMeta> = {
  movie: {
    type: "movie",
    label: "电影",
    channel: "电影频道",
    description: "汇聚院线大片、文艺佳作与类型电影，涵盖科幻、悬疑、动作、剧情、爱情等多种风格，发现值得一看的好电影。",
  },
  tv: {
    type: "tv",
    label: "电视剧",
    channel: "电视剧频道",
    description: "都市情感、古装武侠、悬疑罪案、历史权谋一应俱全，追剧找剧两不误，热门剧集与口碑佳作持续更新。",
  },
  variety: {
    type: "variety",
    label: "综艺",
    channel: "综艺频道",
    description: "音乐竞演、户外挑战、美食旅行、脱口秀等多档综艺节目，轻松欢乐与真挚情感并重，承包你的每段空闲时光。",
  },
};

export const regionOptions = ["全部", "内地", "港台", "美国", "韩国", "日本"];
export const yearOptions = ["全部", "近三年", "往年"];

export type SortKey = "hot" | "new";

export interface FilterState {
  region: string;
  year: string;
  sort: SortKey;
}

export const defaultFilter: FilterState = {
  region: "全部",
  year: "全部",
  sort: "hot",
};

export function isCatalogType(value: string): value is CatalogType {
  return value === "movie" || value === "tv" || value === "variety";
}

export function getCategoryMeta(type: string): CategoryMeta {
  if (isCatalogType(type)) return categoryMetaMap[type];
  return categoryMetaMap.movie;
}

export function getItemsByType(type: CatalogType): CatalogItem[] {
  return allItems.filter((item) => item.type === type);
}

export function getItemById(id: string): CatalogItem | undefined {
  return allItems.find((item) => item.id === id);
}

export function getFeatured(): CatalogItem[] {
  return allItems
    .filter((item) => item.backdrop)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 5);
}

export function getLatest(limit = 12): CatalogItem[] {
  return [...allItems]
    .sort((a, b) => b.year - a.year || b.popularity - a.popularity)
    .slice(0, limit);
}

export function getHot(tab: CatalogType, limit = 8): CatalogItem[] {
  return getItemsByType(tab)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
}

export function filterItems(items: CatalogItem[], filter: FilterState): CatalogItem[] {
  let result = [...items];
  if (filter.region && filter.region !== "全部") {
    result = result.filter((item) => item.region === filter.region);
  }
  if (filter.year === "近三年") {
    result = result.filter((item) => item.year >= 2023);
  } else if (filter.year === "往年") {
    result = result.filter((item) => item.year < 2023);
  }
  if (filter.sort === "new") {
    result.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  } else {
    result.sort((a, b) => b.popularity - a.popularity);
  }
  return result;
}

export function searchItems(keyword: string): CatalogItem[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  const matched = allItems.filter((item) => {
    const haystack = [
      item.title,
      item.categoryLabel,
      item.region,
      item.director,
      item.language,
      ...item.genres,
      ...item.actors,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key) || item.year.toString().includes(key);
  });
  return matched.sort((a, b) => b.popularity - a.popularity);
}

export function getRelated(item: CatalogItem, limit = 6): CatalogItem[] {
  const sameType = allItems.filter((i) => i.id !== item.id && i.type === item.type);
  const scored = sameType
    .map((i) => {
      let score = 0;
      if (i.region === item.region) score += 2;
      score += i.genres.filter((g) => item.genres.includes(g)).length * 3;
      if (i.director === item.director) score += 4;
      return { item: i, score };
    })
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity);
  const result = scored.slice(0, limit).map((s) => s.item);
  if (result.length < limit) {
    const extra = allItems.filter(
      (i) => i.id !== item.id && !result.includes(i) && i.type === item.type
    );
    result.push(...extra.slice(0, limit - result.length));
  }
  return result;
}

export function paginate<T>(items: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    totalPages,
    total: items.length,
    slice: items.slice(start, start + pageSize),
  };
}